import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Assessment as ReportIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import reportsAPI, { ReportType } from '../services/reports';

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  pourId: string;
  pourCode?: string;
  /** Called with a success message after a report is generated/emailed. */
  onSuccess?: (message: string) => void;
  /** Fill the screen (phone callers pass it below sm). */
  fullScreen?: boolean;
  /** Phone layout (below md): the pour code on its own line under the
   *  title, and controls at a 44px touch size. */
  mobileLayout?: boolean;
}

/**
 * Report generation dialog — pick the report type and options (matching the
 * legacy report setup): include graphs, sign report, include attachments, and
 * optionally email the PDF instead of downloading it.
 */
const ReportDialog: React.FC<ReportDialogProps> = ({
  open,
  onClose,
  pourId,
  pourCode,
  onSuccess,
  fullScreen = false,
  mobileLayout = false,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<ReportType>('maturity');
  const [includeGraphs, setIncludeGraphs] = useState(true);
  const [signature, setSignature] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phone layout only: 44px touch targets. Without mobileLayout every
  // control keeps today's size.
  const touchSx = mobileLayout ? { minHeight: 44 } : undefined;
  const checkboxSx = mobileLayout ? { p: 1.25 } : undefined;

  const reset = () => {
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { emailed } = await reportsAPI.generateReport(pourId, {
        type,
        includeGraphs,
        signature,
        includeAttachments,
        email: email.trim() || undefined,
      });
      onSuccess?.(
        emailed
          ? t('reports.emailed', 'Report emailed to {{email}}', { email: email.trim() })
          : t('reports.downloaded', 'Report generated'),
      );
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message || t('reports.failed', 'Failed to generate report'));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle
        sx={{
          ...(mobileLayout ? {} : { display: 'flex', alignItems: 'center', gap: 1 }),
          // Clear the notch when the dialog fills the screen.
          ...(fullScreen ? { pt: 'calc(16px + env(safe-area-inset-top, 0px))' } : {}),
        }}
      >
        {mobileLayout ? (
          <>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportIcon fontSize="small" />
              {t('reports.title', 'Generate Report')}
            </Box>
            {pourCode && (
              <Typography
                component="span"
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere' }}
              >
                {pourCode}
              </Typography>
            )}
          </>
        ) : (
          <>
            <ReportIcon fontSize="small" />
            {t('reports.title', 'Generate Report')}
            {pourCode && (
              <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                — {pourCode}
              </Typography>
            )}
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary">
          {t('reports.reportType', 'Report type')}
        </Typography>
        <ToggleButtonGroup
          value={type}
          exclusive
          fullWidth
          size="small"
          onChange={(_, v) => v && setType(v)}
          sx={{ mt: 0.5, mb: 2 }}
        >
          <ToggleButton value="maturity" sx={touchSx}>{t('reports.maturity', 'Maturity')}</ToggleButton>
          <ToggleButton value="mass" sx={touchSx}>{t('reports.mass', 'Mass Concrete')}</ToggleButton>
        </ToggleButtonGroup>

        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={includeGraphs} onChange={(e) => setIncludeGraphs(e.target.checked)} sx={checkboxSx} />}
            label={t('reports.includeGraphs', 'Include graphs')}
          />
          <FormControlLabel
            control={<Checkbox checked={signature} onChange={(e) => setSignature(e.target.checked)} sx={checkboxSx} />}
            label={t('reports.sign', 'Sign report')}
          />
          <FormControlLabel
            control={<Checkbox checked={includeAttachments} onChange={(e) => setIncludeAttachments(e.target.checked)} sx={checkboxSx} />}
            label={t('reports.includeAttachments', 'Include attachments')}
          />
        </FormGroup>

        <Box sx={{ mt: 1 }}>
          <TextField
            label={t('reports.emailTo', 'Email to (optional)')}
            placeholder="name@example.com"
            type="email"
            size={mobileLayout ? 'medium' : 'small'}
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText={t('reports.emailHelp', 'Leave blank to download instead of emailing')}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          // Clear the iOS home bar when the dialog fills the screen.
          ...(fullScreen ? { pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' } : {}),
        }}
      >
        <Button onClick={handleClose} disabled={loading} sx={touchSx}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading}
          sx={touchSx}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ReportIcon />}
        >
          {email.trim()
            ? t('reports.generateEmail', 'Generate & Email')
            : t('reports.generate', 'Generate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
